import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { HttpService } from 'src/app/services/http.service';
import { ToastrService } from 'ngx-toastr';
import { URLUtils } from 'src/app/urlUtils';
import { forkJoin } from 'rxjs';
import { ConfirmationDialogService } from 'src/app/confirmation-dialog/confirmation-dialog.service';
import { ensureActiveSubscription } from 'src/app/shared/subscription-dialog';
import { HttpErrorResponse } from '@angular/common/http';


@Component({
    selector: 'app-createinvoice',
    templateUrl: './createinvoice.component.html',
    styleUrls: ['./createinvoice.component.scss'],
    standalone: false
})
export class CreateinvoiceComponent implements OnInit {
  // Mode flags
  isEditMode: boolean = false;
  isCreate: boolean = true;
  editInvoiceId: string = '';
  invoiceSequence: number | null = null;

  // Form and UI state
  createinvoiceForm!: FormGroup;
  submitted = false;
  disabled = false;
  shown = false;
  imageLoaded = false;
  
  // Data
  data: any;
  keyword = 'name';
  clients: any = [];
  matterList: any = [];
  billto: any;
  
  // Files and images
  files: any;
  imageToShow: any;
  imageUrl: any = "assets/img/logo.png";
  errorMessage: string = '';
  
  // Date handling
  bsValue: any;
  minDate: any;
  maxDate: any;
  pipe = new DatePipe('en-US');
  selectedClientName: string | null = null;

  
  // Currency
  currencyList = [
    { label: "AustralianDollar (AUD)", code: "AUD", symbol: "A$" },
    { label: "Bahraini Dinar (BHD)", code: "BHD", symbol: ".د.ب" },
    { label: "CanadianDollar (CAD)", code: "CAD", symbol: "C$" },
    { label: "Euro (EUR)", code: "EUR", symbol: "€" },
    { label: "IndianRupee (INR)", code: "INR", symbol: "₹" },
    { label: "JapaneseYen (JPY)", code: "JPY", symbol: "¥" },
    { label: "Kuwaiti Dinar (KWD)", code: "KWD", symbol: "د.ك" },
    { label: "Pound (GBP)", code: "GBP", symbol: "£" },
    { label: "SwissFranc (CHF)", code: "CHF", symbol: "₣" },
    { label: "USDollar (USD)", code: "USD", symbol: "$" }
  ];
  selectedCurrency: any = this.currencyList[4]; // Default INR
  profile:any;
  billingCurrency: string = '';
  
  // Validation patterns
  // gstPattern = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  // panPattern = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  
  // Original invoice data for edit mode
  originalInvoiceData: any = null;
  
  // Modal state
  showSaveModal: boolean = false;
  
  @ViewChild('fileInput') fileInput: any;

  subtotal: number = 0;
  discountAmount: number = 0;
  taxAmount: number = 0;


  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private httpservice: HttpService,
    private toast: ToastrService,
    private cd: ChangeDetectorRef,
    private confirmationDialogService: ConfirmationDialogService
  ) {}

  ngOnInit() {
    this.initializeForm();
    this.getClients();
    this.getLogo();
    
    this.bsValue = new Date();
    
    // Check if edit mode
    this.route.queryParams.subscribe(params => {
      if (params['id']) {
        this.isEditMode = true;
        this.editInvoiceId = params['id'];
        this.loadInvoiceForEdit(this.editInvoiceId);
      } else {
        this.fetchInvoiceId();
         this.getProfile();
      }
    });
    
    // Watch for currency changes
    this.createinvoiceForm.get('currencyCode')?.valueChanges.subscribe((code: string) => {
      const currency = this.currencyList.find(c => c.code === code);
      if (currency) {
        this.selectedCurrency = currency;
      }
    });
    
    // Watch for date changes
    this.createinvoiceForm.get('date')?.valueChanges.subscribe((value: any) => {
      this.updateDueDate(value);
    });

    // Re-validate invoice date vs due date off the FormControls' own
    // valueChanges (not the datepicker's bsValueChange/change events).
    // valueChanges only ever fires once a control's value has actually
    // been committed, so this can never read a stale value the way the
    // event-driven handlers occasionally could.
    this.createinvoiceForm.get('date')?.valueChanges.subscribe(() => {
      this.syncDateDurationErrors();
    });
    this.createinvoiceForm.get('duedate')?.valueChanges.subscribe(() => {
      this.syncDateDurationErrors();
    });

    this.createinvoiceForm.valueChanges.subscribe(() => {
    this.calculateAmounts();
  });
   
  }

  initializeForm() {
    this.createinvoiceForm = this.fb.group({
      invoice_code: ['', [Validators.required]], 
      invoice_sequence: [null],
      gstFirm: [''],
      panFirm: [''],
      gstClient: [''],
      panClient: [''],
      name: ['', [Validators.required]],
      billto: ['', [Validators.required]],
      date: [new Date(), Validators.required],
      duedate: ['', Validators.required],
      // Currency selection is locked to INR for all accounts for now —
      // control starts disabled so no selection can happen in the UI.
      currencyCode: [{ value: 'INR', disabled: true }],
      invoice_items: this.fb.array([this.createInvoiceItem()], [Validators.required]),
      notes: [''],
      tax: ['', [Validators.maxLength(5), Validators.max(100), Validators.min(0), Validators.pattern(/^[0-9]+(\.[0-9]{1,2})?%?$/)]],
      tax_type: ['Tax'],
      discount: ['', [Validators.maxLength(5), Validators.max(100), Validators.pattern(/^[0-9]+(\.[0-9]{1,2})?%?$/), Validators.min(0)]],
      discount_type: ['Discount'],
      matterId: ['']
    });
  }
  createInvoiceItem(): FormGroup {
    return this.fb.group({
      matterId: [''],
      name: ['', Validators.required],
      unitPrice: [0, [this.noZeroStart, Validators.pattern('^[0-9]*$'), Validators.minLength(1), Validators.maxLength(10)]],
      quantity: [1, [this.noZeroStart, Validators.pattern('^[0-9]*$'), Validators.minLength(1), Validators.maxLength(10)]]
      
    });
  }


  noZeroStart(control: AbstractControl): { [key: string]: boolean } | null {
    const value = control.value;
    if (value && value.length > 0 && value[0] === '0') {
      return { 'noZeroStart': true };
    }
    return null;
  }
  onImageError() {
 
  this.shown = false;
  this.imageLoaded = false;
  this.imageToShow = null;
  this.errorMessage = ''; // prevent scary red text
}
onImageLoad() {
  this.imageLoaded = true;
}
  getCurrencyByCountry(country: string): string | null {
  const map: any = {
    India: 'INR',
    Australia: 'AUD',
    Bahrain: 'BHD',
    Canada: 'CAD',
    Japan: 'JPY',
    Kuwait: 'KWD',
    UK: 'GBP',
    Switzerland: 'CHF',
    USA: 'USD'
  };

  return map[country] || null;
}


  getProfile() {
      this.httpservice.sendGetRequest(URLUtils.profile).subscribe(
        (res: any) => {
          if (res.error == false) {
            this.profile = res.data?.details;
            this.billingCurrency = this.profile?.billing_currency ;
            // Currency is locked to INR for all accounts for now, so we
            // intentionally do not derive/apply a currency code from the
            // account's country or billing_currency here.
          }
        });
    }

fetchInvoiceId() {
  this.httpservice.getInvoiceSuggestedId().subscribe({
    next: (res: any) => {
      const invoiceCode = res?.data?.invoice_code;
      const invoiceSequence = res?.data?.invoice_sequence;
      this.invoiceSequence = invoiceSequence;
      this.createinvoiceForm.patchValue({ invoice_code: invoiceCode, invoice_sequence: invoiceSequence });
    },
    error: (err) => {
      console.error("Failed to fetch invoice id", err);
      // this.createinvoiceForm.patchValue({ invoiceCode: this.generateInvoiceNumber() });
    }
  });
}


  // generateInvoiceNumber(): string {
  //   const timestamp = Date.now();
  //   const last6 = timestamp.toString().slice(-6);
  //   return `INV-${last6}`;
  // }

  loadInvoiceForEdit(invoiceId: string) {
  this.httpservice.sendGetRequest(URLUtils.InvoiceWithId(invoiceId)).subscribe(
    (res: any) => {
     // console.log('Edit invoice data:', res);

      if (res.error === false) {
        const invoice = res?.invoice;
        this.originalInvoiceData = JSON.parse(JSON.stringify(invoice)); // Deep clone

        // Set currency
        const code = invoice?.currency_code;
        const foundCurrency = this.currencyList.find(c => c.code === code);
        if (foundCurrency) {
          this.selectedCurrency = foundCurrency;
        }

        // Set logo
        this.shown = invoice.include_logo;

        // Clear existing items
        const itemsArray = this.createinvoiceForm.get('invoice_items') as FormArray;
        while (itemsArray.length) {
          itemsArray.removeAt(0);
        }

        // Add items from invoice
        if (invoice.items && invoice.items.length > 0) {
          invoice.items.forEach((item: any) => {
            const itemGroup = this.fb.group({
              matterId: [item.matter_id || ''],
              name: [item.name || '', Validators.required],
              unitPrice: [
                item.unit_price || 0,
                [this.noZeroStart, Validators.pattern('^[0-9]*$')]
              ],
              quantity: [
                item.no_of_units || 1,
                [this.noZeroStart, Validators.pattern('^[0-9]*$')]
              ]
            });
            itemsArray.push(itemGroup);
          });
        }

        // Persist selected client name
        this.selectedClientName = invoice.name;

        // Parse dates
        const invoiceDate = invoice.date ? new Date(invoice.date) : new Date();
        const dueDate = invoice.duedate ? new Date(invoice.duedate) : '';

        // Patch main form values
        this.createinvoiceForm.patchValue({
          invoice_code: invoice.invoice_no || '',
          gstFirm: invoice.firm_gst_no || '',
          panFirm: invoice.firm_pan_no || '',
          gstClient: invoice.client_gst_no || '',
          panClient: invoice.client_pan_no || '',
          name: invoice.name || '',
          billto: invoice.billto || '',
          date: invoiceDate,
          duedate: dueDate,
          currencyCode: invoice.currency_code || 'INR',
          notes: invoice.notes || '',
          tax: invoice.tax_items?.[0]?.tax || '',
          tax_type: invoice.tax_items?.[0]?.tax_type || 'Tax',
          discount: invoice.discounts?.[0]?.discount || '',
          discount_type: invoice.discounts?.[0]?.discount_type || 'Discount',
          matterId: ''
        });

        this.billto = invoice.billto;
        this.createinvoiceForm.get('billto')?.setValue(this.billto, { emitEvent: false });

        // Load client + matters
        if (invoice.name && invoice.docid) {
          const matchingClient = this.data?.find(
            (client: any) => client.name === invoice.name
          );

          if (matchingClient) {
            this.billto = invoice.billto ?? matchingClient.address;

            this.clients = [{
              id: matchingClient.id,
              type: matchingClient.type
            }];

            this.selectedClientName = matchingClient.name;

            this.createinvoiceForm.patchValue({
              billto: this.billto
            });

            // Load matters for client
            this.getMattersByClient(matchingClient.id, () => {
              //console.log('Matters loaded:', this.matterList);

              // ✅ Set matter by ID (reliable)
              if (invoice.matters && invoice.matters.length > 0) {
                const matterId = invoice.matters[0].id;

                const matchingMatter = this.matterList.find(
                  (m: any) => m.id === matterId
                );

                if (matchingMatter) {
                  this.createinvoiceForm.patchValue({
                    matterId: matchingMatter.id
                  });
                  //console.log('Selected matter set:', matchingMatter.id);
                }
              }
            });
          }
        }
      }
    },
    (error) => {
      console.error('Error loading invoice:', error);
      this.toast.error('Failed to load invoice data');
      this.router.navigate(['/invoice']);
    }
  );
}

  onSubmit() {
    if (!ensureActiveSubscription(this.confirmationDialogService)) {
      return;
    }
    
    this.submitted = true;

    const itemsArray = this.createinvoiceForm.get('invoice_items') as FormArray;
    itemsArray.controls.forEach(item => {
      item.get('name')?.markAsTouched();
    });
    if (!this.createinvoiceForm.get('billto')?.value?.trim()) {
        // this.toast.error('Client address is required');
        this.createinvoiceForm.get('billto')?.markAsTouched();
        return;
      }
    
    if (!this.createinvoiceForm.value.date || !this.createinvoiceForm.value.duedate) {
      return;
    }
    
    if (this.createinvoiceForm.value.tax === '') {
      this.setValue("tax", 0);
    }
    
    if (this.createinvoiceForm.value.discount === '') {
      this.setValue("discount", 0);
    }
    
    const invoiceItems = this.createinvoiceForm.get('invoice_items') as FormArray;
    invoiceItems.controls.forEach(item => {
      const rateCtrl = item.get('unitPrice');
      const qtyCtrl = item.get('quantity');
      
      if (!rateCtrl?.value || rateCtrl.value === '') {
        rateCtrl?.setValue(0);
      }
      
      if (!qtyCtrl?.value || qtyCtrl.value === '') {
        qtyCtrl?.setValue(1);
      }
    });
    
    if (this.createinvoiceForm.valid && !this.errorMessage || this.clients.length !== 0) {
      if (this.isEditMode) {
        // Show modal for Replace vs Save Both
        this.showSaveModal = true;
      } else {
        // Create new invoice
        this.saveInvoice(false);
      }
    }

  }

  handleModalAction(action: 'replace' | 'saveboth') {
    this.showSaveModal = false;
    
    if (action === 'replace') {
      this.saveInvoice(false); // Replace existing
    } else {
      this.saveInvoice(true); // Save as new
    }
  }

  saveInvoice(saveAsNew: boolean) {
    this.disabled = true;
    
    let selectedMatterId = this.createinvoiceForm.value.matterId || null;
    let matters: any[] = [];
    
    if (selectedMatterId) {
      const selectedMatter = this.matterList.find((m: any) => m.id === selectedMatterId);
      if (selectedMatter) {
        matters.push({
          id: selectedMatter.id,
          name: selectedMatter.title,
          type: selectedMatter.type
        });
      }
    }

    let invoiceCode = this.createinvoiceForm.value.invoice_code;
    
    const formName =
    this.createinvoiceForm.value.name?.trim() ||
    this.selectedClientName ||
    this.clients?.[0]?.name ||
    '';

  const formBillto = this.createinvoiceForm.value.billto;
    
    const payload = {
      invoice_code: invoiceCode, 
      invoice_sequence: this.createinvoiceForm.value.invoice_sequence,
      firm_gst_no: this.createinvoiceForm.value.gstFirm,
      firm_pan_no: this.createinvoiceForm.value.panFirm,
      client_gst_no: this.createinvoiceForm.value.gstClient,
      client_pan_no: this.createinvoiceForm.value.panClient,
      name: formName,
      billto: formBillto,
      notes: this.createinvoiceForm.value.notes,
      info: {
        date: this.pipe.transform(this.createinvoiceForm.value.date, 'yyyy-MM-dd'),
        duedate: this.pipe.transform(this.createinvoiceForm.value.duedate, 'yyyy-MM-dd')
      },
      invoice_items: this.createinvoiceForm.value.invoice_items.map((item: any) => ({
        name: item.name,
        unitPrice: item.unitPrice,
        quantity: item.quantity
      })),
      currency_code: this.selectedCurrency?.code,
      tax_items: [{
        tax_type: this.createinvoiceForm.value.tax_type,
        amount: this.calculateTotalAmount(),
        tax: this.createinvoiceForm.value.tax
      }],
      discounts: [{
        discount_type: this.createinvoiceForm.value.discount_type,
        amount: this.calculateTotalAmount(),
        discount: this.createinvoiceForm.value.discount
      }],
      client: this.clients,
      matters: matters,
      include_logo: this.shown
    };

    if (this.files) {
      let fdata = new FormData();
      fdata.append('file', this.files);
      
      this.httpservice.sendPostRequest(URLUtils.UploadLogo, fdata).subscribe(
        (res: any) => {
          if (res.error === false) {
            this.performSave(payload, saveAsNew);
          }
        },
        (error: HttpErrorResponse) => {
          this.disabled = false;
          this.handleError(error);
        }
      );
    } else {
      this.performSave(payload, saveAsNew);
    }
  }

  performSave(payload: any, saveAsNew: boolean) {
     //console.log('!!payload',payload)
      
      if (!payload.client || payload.client.length === 0) {
        payload.client = this.clients;
      }

    if (this.isEditMode && !saveAsNew) {
      // PATCH for replace
      const diff = this.computeDiff(this.originalInvoiceData, payload);
      diff.replace_original = true;
      // console.log('Saving as new with payload:', payload);
      // console.log('Original invoice data for diff:', this.originalInvoiceData);
      
      // console.log('Sending PATCH with diff:', diff);
      
      this.httpservice.sendPatchRequest(URLUtils.InvoiceWithId(this.editInvoiceId), diff).subscribe(
        (res: any) => {
          this.disabled = false;
          if (res.error === false) {
            this.toast.success("Invoice Updated Successfully");
            this.router.navigate(['/invoice']);
          } else {
            this.toast.error(this.extractErrorMessage(res));
          }
        },
        (error: HttpErrorResponse) => {
          this.disabled = false;
          this.handleError(error);
        }
      );
    } else if(this.isEditMode && saveAsNew) {
      // POST for new or save both
      //console.log('Saving as new with payload:', payload);
      //console.log('Original invoice data for diff:', this.originalInvoiceData);
      const diff = this.computeDiff(this.originalInvoiceData, payload);
      diff.replace_original = false;

      this.httpservice.sendPatchRequest(URLUtils.InvoiceWithId(this.editInvoiceId), diff).subscribe(
        (res: any) => {
          this.disabled = false;
          if (res.error === false) {
            const message = saveAsNew ? "New Invoice Created Successfully" : "Invoice Created Successfully";
            this.toast.success(message);
            this.router.navigate(['/invoice']);
          } else {
            this.toast.error(this.extractErrorMessage(res));
          }
        },
        (error: HttpErrorResponse) => {
          this.disabled = false;
          this.handleError(error);
        }
      );
    } else {
      // POST for new or save both
    

      this.httpservice.sendPostRequest(URLUtils.Invoice, payload).subscribe(
        (res: any) => {
          this.disabled = false;
          if (res.error === false) {
            const message = saveAsNew ? "New Invoice Created Successfully" : "Invoice Created Successfully";
            this.toast.success(message);
            this.router.navigate(['/invoice']);
          } else {
            this.toast.error(this.extractErrorMessage(res));
          }
        },
        (error: HttpErrorResponse) => {
          this.disabled = false;
          this.handleError(error);
        }
      );
    } 
  }

  computeDiff(original: any, updated: any): any {
  const diff: any = {};

  const fieldMap: any = {
    invoice_code: 'invoice_code',
    billto: 'billto',
    notes: 'notes',
    currency_code: 'currency_code',
    include_logo: 'include_logo'
  };

  for (const key in fieldMap) {
    const oKey = fieldMap[key];
    if (updated[key] !== undefined && original[oKey] !== updated[key]) {
      diff[oKey] = updated[key];
    }
  }

  const formatDate = (date: string | Date | null) => {
    if (!date) return null;
    
    // If it's already a string in YYYY-MM-DD format, return it
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    
    // Convert to Date object and format
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return null;
    
    return dateObj.toISOString().split('T')[0];
  };

  // ---------- DATE & DUE DATE (INSIDE INFO) ----------
  // const originalDate = formatDate(original?.date);
  // const updatedDate = updated?.info?.date
  //   ? formatDate(updated.info.date)
  //   : originalDate;

  // const originalDue = formatDate(original?.duedate);
  // const updatedDue = updated?.info?.duedate
  //   ? formatDate(updated.info.duedate)
  //   : originalDue;

  // if (originalDate !== updatedDate || originalDue !== updatedDue) {
  //   diff.info = diff.info || {};

  //   if (originalDate !== updatedDate) {
  //     diff.info.date = updatedDate;
  //   }

  //   if (originalDue !== updatedDue) {
  //     diff.info.duedate = updatedDue;
  //   }
  // }
  const pick = (obj: any, paths: string[]) =>
    paths.map(p => p.split('.').reduce((o, k) => o?.[k], obj))
         .find(v => v !== undefined);
 const date  = pick(original, ['info.date','date']);
  const uDate = pick(updated,  ['info.date','date']);

  const due   = pick(original, ['info.duedate','duedate']);
  const uDue  = pick(updated,  ['info.duedate','duedate']);

  if (date !== uDate || due !== uDue) {
    diff.info = {};
    if (date !== uDate) diff.info.date = uDate;
    if (due  !== uDue)  diff.info.duedate = uDue;
  }
  // ---------- ITEMS ----------
  const itemsChanged =
    JSON.stringify(original.items ?? []) !==
    JSON.stringify(updated.invoice_items ?? []);

  if (itemsChanged) {
    diff.invoice_items = updated.invoice_items.map((i: any) => ({
      name: i.name,
      unitPrice: i.unitPrice,
      quantity: i.quantity
    }));
  }

  // ---------- TAX ----------
  const originalTax = original.tax_items?.[0] || null;
  const updatedTax = updated.tax_items?.[0] || null;

  if (JSON.stringify(originalTax) !== JSON.stringify(updatedTax)) {
    diff.tax_items = [updatedTax];
  }

  // ---------- DISCOUNT ----------
  const originalDiscount = original.discounts?.[0] || null;
  const updatedDiscount = updated.discounts?.[0] || null;
  
  if (JSON.stringify(originalDiscount) !== JSON.stringify(updatedDiscount)) {
    diff.discounts = [updatedDiscount];
  }

  // ---------- MATTERS ----------
  const originalMatters = original.matters ?? [];
  const updatedMatters = updated.matters ?? [];

  if (JSON.stringify(originalMatters) !== JSON.stringify(updatedMatters)) {
    diff.matters = updatedMatters;
  }

  // ---------- CLIENT ----------
  if (updated.client && updated.client.length > 0) {
    diff.client = updated.client;
  }
  
  if (updated.firm_gst_no !== undefined && original.firm_gst_no !== updated.firm_gst_no) {
    diff.firm_gst_no = updated.firm_gst_no;
  }

  if (updated.firm_pan_no !== undefined && original.firm_pan_no !== updated.firm_pan_no) {
    diff.firm_pan_no = updated.firm_pan_no;
  }

  if (updated.client_gst_no !== undefined && original.client_gst_no !== updated.client_gst_no) {
    diff.client_gst_no = updated.client_gst_no;
  }

  if (updated.client_pan_no !== undefined && original.client_pan_no !== updated.client_pan_no) {
    diff.client_pan_no = updated.client_pan_no;
  }
  diff.name = updated.name || original.name;

  //console.log('Computed diff:', diff);
  return diff;
}


  generateSequentialInvoiceNumber(baseInvoiceCode: string): string {
    // Remove existing sequence if any
    const baseNumber = baseInvoiceCode.replace(/\(\d+\)$/, '').trim();
    
    // Check if original already has a sequence
    const match = this.originalInvoiceData?.invoice_code?.match(/\((\d+)\)$/);
    const currentSeq = match ? parseInt(match[1]) : 0;
    const newSeq = currentSeq + 1;
    
    return `${baseNumber}(${newSeq})`;
  }

  handleError(error: HttpErrorResponse) {
    const errorMessage = this.extractErrorMessage(error.error);
    this.toast.error(errorMessage);
  }

  /**
   * Backend returns errors in one of two shapes:
   *  - { error: true, msg: '...' }
   *  - { error: true, errors: [{ field: '...', msg: '...' }, ...] }
   * This normalizes both into a single display string so the toast
   * never shows blank, and combines multiple field errors if present.
   */
  private extractErrorMessage(payload: any): string {
    if (payload?.errors && Array.isArray(payload.errors) && payload.errors.length > 0) {
      return payload.errors
        .map((e: any) => e?.msg)
        .filter((msg: any) => !!msg)
        .join(' ');
    }
    if (payload?.msg) {
      return payload.msg;
    }
    return 'Something went wrong. Please try again.';
  }

  onClose(): void {
    if (this.hasFormChanged() && this.createinvoiceForm.dirty) {
      this.confirmationDialogService
        .confirm(
          'Alert',
          'Changes you made will not be saved. Do you want to continue?',
          true,
          'Yes',
          'No',
          false,
          'sm',
          true
        )
        .then((confirmed) => {
          if (confirmed) {
            this.router.navigate(['/invoice']);
          }
        })
        .catch(() => {});
    } else {
      this.router.navigate(['/invoice']);
    }
  }

  hasFormChanged(): boolean {
    const invoiceItems = this.createinvoiceForm.value.invoice_items || [];
    
    const anyItemFilled = invoiceItems.some((item: any) => item.name?.trim());
    
    return [
      this.clients,
      this.billto,
      this.createinvoiceForm.value.invoiceCode,
      this.createinvoiceForm.value.gstFirm,
      this.createinvoiceForm.value.panFirm,
      this.createinvoiceForm.value.gstClient,
      this.createinvoiceForm.value.panClient
    ].some(val => val !== null && val !== '' && val !== undefined) || anyItemFilled;
  }

  // ===== Form Controls =====
  
  setValue(controlName: string, value: any) {
    this.createinvoiceForm.get(controlName)?.setValue(value);
  }

  addItem() {
    const invoiceItems = this.createinvoiceForm.get('invoice_items') as FormArray;
    let newItem = this.createInvoiceItem();

    invoiceItems.push(newItem);    
    
    setTimeout(() => {
      const textareas = document.querySelectorAll('.description-textarea');
      const lastTextarea = textareas[textareas.length - 1] as HTMLTextAreaElement;
      if (lastTextarea) {
        lastTextarea.focus();
      }
    });
  }

  removeItem(index: number) {
    if (this.createinvoiceForm.value.invoice_items.length === 1) {
      this.toast.warning('At least one item is required.');
      return;
    }
    const invoiceItems = this.createinvoiceForm.get('invoice_items') as FormArray;
    invoiceItems.removeAt(index);
  }

  // ===== Client & Matter =====
  
  selectEvent(item: any) {
  this.billto = item.address || '';

  this.createinvoiceForm.patchValue({
    name: item?.name || '',
    billto: this.billto
  });

  // Force validation
  const billtoCtrl = this.createinvoiceForm.get('billto');
  billtoCtrl?.markAsTouched();
  billtoCtrl?.updateValueAndValidity();

  // Reset & load matters
  this.clients = [{ id: item.id, type: item.type }];
  this.createinvoiceForm.patchValue({ matterId: '' });
  this.getMattersByClient(item.id);
}

  onChangeSearch(val: any) {
  if (!val) {
    this.clients = [];
    this.billto = '';

    this.createinvoiceForm.patchValue({
      billto: ''
    });

    this.createinvoiceForm.get('billto')?.markAsTouched();
    this.createinvoiceForm.get('billto')?.updateValueAndValidity();
  }
}


  onFocused(e: any) {}

  getClients() {
    forkJoin([
      this.httpservice.getFeaturesdata(URLUtils.getAllRelationship),
      this.httpservice.getFeaturesdata(URLUtils.getCalenderExternal)
    ]).subscribe(([entityRes, corporateRes]: [any, any]) => {
      const entityClients = entityRes?.data?.relationships;
      const corporateClients = corporateRes?.relationships.map((obj: any) => ({
        "id": obj.id,
        "type": "corporate",
        "name": obj.name,
        "address": obj.address
      }));
      this.data = [...entityClients, ...corporateClients];
    });
  }

   getMattersByClient(clientId: any, callback?: () => void) {
    if (!clientId) {
      return;
    }
    
    this.httpservice.sendGetRequest(URLUtils.getInMattersClient(clientId)).subscribe(
      (res: any) => {
        this.matterList = res?.matterList || [];
        if (callback) {
          callback();
        }
      },
      (error) => {
        console.error('Matter API error:', error);
        this.matterList = [];
        if (callback) {
          callback();
        }
      }
    );
  }

  // ===== Logo =====
  
  getLogo() {
    this.httpservice.sendGetRequest(URLUtils.GetLogo).subscribe((res: any) => {
      if (res.error === false) {
        this.imageToShow = res?.data?.url;
        this.imageUrl = res?.data?.url;
        this.shown = true;
      }
    });
  }

  onSelectFile(event: any) {
    this.shown = true;
    this.imageUrl = '';
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/bmp'];
    const maxSize = 1 * 1024 * 1024;
    const file = event.target.files[0];
    
    if (!allowedTypes.includes(file.type)) {
      this.imageUrl = "assets/img/logo.png";
      this.imageToShow = this.imageUrl;
      this.errorMessage = 'Invalid image format. Only JPEG, JPG, PNG allowed';
      return;
    }
    
    if (file.size > maxSize) {
      this.imageUrl = "assets/img/logo.png";
      this.imageToShow = this.imageUrl;
      this.errorMessage = 'Max file size should be less than 1 MB';
      return;
    }
    
    this.files = event.target.files[0];
    this.errorMessage = '';
    
    if (event.target.files && event.target.files[0]) {
      var reader = new FileReader();
      reader.readAsDataURL(event.target.files[0]);
      reader.onload = (event) => {
        this.imageToShow = event.target?.result;
      };
    }
  }

  removeImg() {
    this.shown = false;
    this.imageUrl = "assets/img/logo.png";
    this.errorMessage = '';
    this.files = '';
  }

  // ===== Currency =====
  
  onCurrencyChange(event: any) {
    const code = event.target.value;
    const currency = this.currencyList.find(c => c.code === code);
    if (currency) {
      this.selectedCurrency = currency;
    }
  }

  // ===== Date Handling =====
  
  updateDueDate(date: Date) {
    if (date) {
      this.createinvoiceForm.get('duedate')?.setValue(null);
      this.createinvoiceForm.get('duedate')?.enable();
    } else {
      this.createinvoiceForm.get('duedate')?.reset();
      this.createinvoiceForm.get('duedate')?.disable();
    }
  }

  /**
   * Single source of truth for invoice-date/due-date consistency.
   * Recomputes from the live values of BOTH controls and sets/clears
   * errors on BOTH together, so correcting either field always
   * clears any stale error left on the other control.
   */
  private syncDateDurationErrors() {
    const date = this.date.value;
    const duedate = this.duedate.value;

    if (date && duedate && duedate < date) {
      this.date.setErrors({ 'invalidDuration': true });
      this.duedate.setErrors({ 'invalidDurationof': true });
    } else if (date && duedate && +duedate === +date) {
      this.date.setErrors({ 'invalidEqual': true });
      this.duedate.setErrors({ 'invalidEqual': true });
    } else {
      this.date.setErrors(null);
      this.duedate.setErrors(null);
    }
  }

  selectDuration() {
    this.syncDateDurationErrors();
    this.cd.detectChanges();
  }

  selectDurationIn() {
    this.syncDateDurationErrors();
    this.cd.detectChanges();
  }
  

  selectClearIn() {
    this.syncDateDurationErrors();
  }

  selectClearDue() {
    this.syncDateDurationErrors();
  }

  clearDueDateError() {
    // Kept for existing template bindings; error state is now fully
    // managed by syncDateDurationErrors() above, so this is a no-op.
  }

  clearInDateError() {
    // Kept for existing template bindings; error state is now fully
    // managed by syncDateDurationErrors() above, so this is a no-op.
  }

  get date() {
    return this.createinvoiceForm.get('date')!;
  }

  get duedate() {
    return this.createinvoiceForm.get('duedate')!;
  }

  get f() {
    return this.createinvoiceForm.controls;
  }

  get invoiceItemsArray(): FormArray {
    return this.createinvoiceForm.get('invoice_items') as FormArray;
  }

  // ===== Save button state (disable-on-error + tooltip) =====

  get isSaveDisabled(): boolean {
    return this.disabled || this.createinvoiceForm.invalid;
  }

  /**
   * Meaningful, specific reason the Save button is disabled, shown in a
   * tooltip. Checked in the same rough order a user would encounter the
   * fields top-to-bottom; falls back to a generic message if nothing
   * more specific applies.
   */
  get saveDisabledReason(): string {
    if (this.disabled) {
      return 'Saving in progress…';
    }
    if (!this.createinvoiceForm.invalid) {
      return '';
    }
    if (
      this.date.errors?.['invalidDuration'] ||
      this.date.errors?.['invalidEqual'] ||
      this.duedate.errors?.['invalidDurationof'] ||
      this.duedate.errors?.['invalidEqual']
    ) {
      return "Due Date can't be earlier than Invoice Date";
    }
    if (this.date.invalid) {
      return 'Please select the Invoice Date';
    }
    if (this.duedate.invalid) {
      return 'Please select the Due Date';
    }
    if (
      this.createinvoiceForm.get('name')?.invalid ||
      this.createinvoiceForm.get('billto')?.invalid
    ) {
      return 'Please select a client';
    }
    if (this.invoiceItemsArray.invalid) {
      return 'Please complete all invoice item details';
    }
    if (this.createinvoiceForm.get('invoice_code')?.invalid) {
      return 'Invoice Number is required';
    }
    if (this.createinvoiceForm.get('tax')?.invalid) {
      return 'Please enter a valid tax value';
    }
    if (this.createinvoiceForm.get('discount')?.invalid) {
      return 'Please enter a valid discount value';
    }
    return 'Please complete all required fields correctly before saving';
  }

  // ===== Calculations =====
  
  calculateAmount(item: FormGroup): number {
    const quantity = item.get('quantity')?.value;
    const unitPrice = item.get('unitPrice')?.value;
    
    if (isNaN(quantity) || isNaN(unitPrice)) {
      return 0;
    }
    
    const totalAmount = quantity * unitPrice;
    const roundedAmount = Number(totalAmount.toFixed(2));
    return roundedAmount;
  }

  calculateTotalAmount(): number {
    const invoiceItems = this.createinvoiceForm.get('invoice_items') as FormArray;
    let totalAmount = 0;
    
    for (let i = 0; i < invoiceItems.length; i++) {
      const item = invoiceItems.at(i) as FormGroup;
      totalAmount += this.calculateAmount(item);
    }
    
    return Number(totalAmount.toFixed(2));
  }

  calculateTotalAmountWithOthers(): number {
    const invoiceItems = this.createinvoiceForm.get('invoice_items') as FormArray;
    let totalAmount = 0;
    const taxPercentage = this.createinvoiceForm.get('tax')?.value || 0;
    const discountPercentage = this.createinvoiceForm.get('discount')?.value || 0;
    
    for (let i = 0; i < invoiceItems.length; i++) {
      const item = invoiceItems.at(i) as FormGroup;
      totalAmount += this.calculateAmount(item);
    }
    
    let amountAfterDiscount = totalAmount;
    if (discountPercentage) {
      const discountAmount = (totalAmount * discountPercentage) / 100;
      amountAfterDiscount -= discountAmount;
    }
    
    const taxAmount = (amountAfterDiscount * taxPercentage) / 100;
    const amountAfterTax = amountAfterDiscount + taxAmount;
    
    return Number(amountAfterTax.toFixed(2));
  }

  // ===== Input Handlers =====
  
  sanitizeInput(event: any) {
    const regex = /[^A-Za-z0-9\s.,!?:;'~"@&%#+=*()[\]`/-_|]|^\s/g;
    event.target.value = event.target.value.replace(regex, '').slice(0, 250);
  }

  restrictSpaces(event: any) {
    let inputValue: string = event.target.value;
    inputValue = inputValue.replace(/\s{2,}/g, ' ');
    event.target.value = inputValue;
  }

  onInput(event: Event, field: string, index: number, item: FormGroup): void {
    const inputElement = event.target as HTMLInputElement;
    inputElement.value = inputElement.value.replace(/[^0-9]/g, '');
    
    if (inputElement.value.startsWith('0')) {
      inputElement.value = inputElement.value.substring(1);
    }
    
    this.restrictFirstPosition(event);
    
    if (inputElement.value) {
      inputElement.classList.add('has-value');
    } else {
      inputElement.classList.remove('has-value');
    }
    
    item.get(field)?.setValue(inputElement.value);
  }

  restrictFirstPosition(event: any): void {
    const input = event.target.value;
    if (input && input[0] === '0') {
      event.target.value = input.substring(1);
    }
  }

  onInputDT(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    inputElement.value = inputElement.value.replace(/[^0-9.]/g, '');
    
    const dotCount = (inputElement.value.match(/\./g) || []).length;
    if (dotCount > 1) {
      inputElement.value = inputElement.value.substring(0, inputElement.value.lastIndexOf('.'));
    }
    
    this.restrictFirstPositionDT(event);
    
    if (inputElement.value) {
      inputElement.classList.add('has-value');
    } else {
      inputElement.classList.remove('has-value');
    }
  }

  restrictFirstPositionDT(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    if (inputElement.value.startsWith('0') && inputElement.value[1] !== '.') {
      inputElement.value = inputElement.value.substring(1);
    }
  }

  discountVal(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    const trimmedValue = inputElement.value.trim();
    inputElement.value = trimmedValue;
    
    if (!trimmedValue.match(/^(?!0$)\d+(\.\d{0,2})?$/) && trimmedValue !== '') {
      inputElement.value = '';
    }
    
    const discountControl = this.createinvoiceForm.get('discount');
    if (discountControl) {
      discountControl.setValue(inputElement.value, { emitEvent: false });
      discountControl.updateValueAndValidity();
    }

    this.calculateAmounts(); //show rate
  }
   asFormGroup(control: AbstractControl): FormGroup {
    return control as FormGroup;
  }

  taxVal(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    const trimmedValue = inputElement.value.trim();
    inputElement.value = trimmedValue;
    
    if (!trimmedValue.match(/^(?!0$)\d+(\.\d{0,2})?$/) && trimmedValue !== '') {
      inputElement.value = '';
    }
    
    const taxControl = this.createinvoiceForm.get('tax');
    if (taxControl) {
      taxControl.setValue(inputElement.value, { emitEvent: false });
      taxControl.updateValueAndValidity();
    }
    this.calculateAmounts(); //show rate
  }

calculateAmounts() {
  const subtotal = this.calculateTotalAmount();

  const discountPercent = Number(this.createinvoiceForm.get('discount')?.value) || 0;
  const taxPercent = Number(this.createinvoiceForm.get('tax')?.value) || 0;

  // Discount amount
  this.discountAmount = (subtotal * discountPercent) / 100;

  // After discount
  const afterDiscount = subtotal - this.discountAmount;

  // Tax amount
  this.taxAmount = (afterDiscount * taxPercent) / 100;
}


}